import React, { FunctionComponent } from 'react';
import { Box, Text } from 'rebass/styled-components';
import { MarkupException, MarkupVariable } from '.';
import {
  lookupExceptionStr,
  parseConditionToWords,
  PolicyConditionProps,
  UNDEFINED_POLICY_VARIABLE_DISPLAY,
} from '..';
import { variableIndexRegEx } from '../../../util';
import { replaceVariableIndexWithValue } from '../util/helpers';

const PolicyCondition: FunctionComponent<PolicyConditionProps> = ({
  condition,
  variables,
  transientVariables,
  valueMap = {},
  exceptionsMap = {},
  isEmphasizedPolicyResult,
}: PolicyConditionProps) => {
  const wordsArr = parseConditionToWords({
    condition,
    variables,
    transientVariableNames: Object.keys(transientVariables || {}),
    valueMap,
  });
  const jsxArr: Array<React.ReactElement> = [];

  // The condition is now broken into words, some of which we will
  // mark up for hover, some we will just wrap
  wordsArr?.forEach((word: string, wordsIdx: number) => {
    if (variableIndexRegEx.test(word)) {
      // test to see if we are looking at a variable
      const { variableStr, val } = replaceVariableIndexWithValue(
        word, variables, transientVariables, valueMap,
      );

      if (variableStr && val != null) {
        // if we have a value in var map
        jsxArr.push(
          <MarkupVariable
            variable={variableStr}
            value={val}
            isEmphasizedPolicyResult={isEmphasizedPolicyResult}
            key={wordsIdx}
          />,
        );
      } else if (variableStr) {
        // else we have either undefined or error
        const exceptionValue = lookupExceptionStr(variableStr, exceptionsMap);
        if (exceptionValue) {
          jsxArr.push(
            <MarkupException
              variable={variableStr}
              value={exceptionValue}
              isEmphasizedPolicyResult={isEmphasizedPolicyResult}
              key={wordsIdx}
            />,
          );
        } else {
          jsxArr.push(
            <MarkupVariable
              variable={variableStr}
              value={UNDEFINED_POLICY_VARIABLE_DISPLAY}
              isEmphasizedPolicyResult={isEmphasizedPolicyResult}
              key={wordsIdx}
            />,
          );
        }
      }
    } else {
      // this is not a variable
      jsxArr.push(
        <Text
          variant={isEmphasizedPolicyResult ? 'primaryBold' : 'primary'}
          sx={{ display: 'inline', whiteSpace: 'pre-wrap' }}
          key={wordsIdx}
        >
          {word}
        </Text>,
      );
    }
  });

  return <Box>{jsxArr}</Box>;
};

export default PolicyCondition;
